import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_ENDPOINTS } from '../config/api';

const AdminFeeManagement = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('structures');
  const [loading, setLoading] = useState(false);

  // Fee Structures State
  const [feeStructures, setFeeStructures] = useState([]);
  const [showAddFeeModal, setShowAddFeeModal] = useState(false);
  const [editingFee, setEditingFee] = useState(null);
  const [newFee, setNewFee] = useState({
    name: '',
    description: '',
    amount: '',
    category: 'academic',
    isMandatory: false,
    frequency: 'one-time',
    applicableClasses: []
  });

  // Payments State
  const [payments, setPayments] = useState([]);
  const [filteredPayments, setFilteredPayments] = useState([]);
  const [paymentFilters, setPaymentFilters] = useState({
    status: 'all',
    feeType: 'all',
    student: '',
    startDate: '',
    endDate: ''
  });
  const [showRecordPaymentModal, setShowRecordPaymentModal] = useState(false);
  const [newPayment, setNewPayment] = useState({
    username: '',
    feeType: '',
    amount: '',
    paymentMethod: 'Cash',
    transactionId: '',
    notes: ''
  });

  // Analytics State
  const [analytics, setAnalytics] = useState(null);
  const [defaulters, setDefaulters] = useState([]);

  // Bulk Operations State
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [bulkOperation, setBulkOperation] = useState('');

  // Classes for selection
  const [classes, setClasses] = useState([]);

  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    setLoading(true);
    try {
      // Load fee structures
      const structuresRes = await fetch(`${API_ENDPOINTS.BASE_URL}/api/fees/structures`);
      if (structuresRes.ok) {
        const structures = await structuresRes.json();
        setFeeStructures(structures);
      }

      // Load classes
      const classesRes = await fetch(`${API_ENDPOINTS.CLASSES}`);
      if (classesRes.ok) {
        const classesData = await classesRes.json();
        setClasses(classesData);
      }

      // Load payments
      await loadPayments();

      // Load analytics
      await loadAnalytics();

    } catch (error) {
      console.error('Error loading initial data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadPayments = async () => {
    try {
      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/api/fees/payments`);
      if (response.ok) {
        const paymentsData = await response.json();
        setPayments(paymentsData);
        setFilteredPayments(paymentsData);
      }
    } catch (error) {
      console.error('Error loading payments:', error);
    }
  };

  const loadAnalytics = async () => {
    try {
      const [summaryRes, defaultersRes] = await Promise.all([
        fetch(`${API_ENDPOINTS.BASE_URL}/api/fees/analytics/summary`),
        fetch(`${API_ENDPOINTS.BASE_URL}/api/fees/analytics/defaulters`)
      ]);

      if (summaryRes.ok) {
        const summary = await summaryRes.json();
        setAnalytics(summary);
      }

      if (defaultersRes.ok) {
        const defaultersData = await defaultersRes.json();
        setDefaulters(defaultersData);
      }
    } catch (error) {
      console.error('Error loading analytics:', error);
    }
  };

  // Fee Structure Management
  const handleSaveFee = async () => {
    if (!newFee.name || !newFee.amount || !newFee.category) {
      alert('Please fill in all required fields');
      return;
    }

    try {
      const url = editingFee
        ? `${API_ENDPOINTS.BASE_URL}/api/fees/structures/${editingFee.id}`
        : `${API_ENDPOINTS.BASE_URL}/api/fees/structures`;

      const method = editingFee ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newFee)
      });

      if (response.ok) {
        alert(`Fee structure ${editingFee ? 'updated' : 'created'} successfully!`);
        setShowAddFeeModal(false);
        setEditingFee(null);
        resetNewFee();
        loadInitialData();
      } else {
        alert('Failed to save fee structure');
      }
    } catch (error) {
      console.error('Error saving fee structure:', error);
      alert('Failed to save fee structure');
    }
  };

  const handleDeleteFee = async (feeId) => {
    if (!window.confirm('Are you sure you want to delete this fee structure?')) {
      return;
    }

    try {
      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/api/fees/structures/${feeId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        alert('Fee structure deleted successfully!');
        loadInitialData();
      } else {
        alert('Failed to delete fee structure');
      }
    } catch (error) {
      console.error('Error deleting fee structure:', error);
      alert('Failed to delete fee structure');
    }
  };

  const handleEditFee = (fee) => {
    setEditingFee(fee);
    setNewFee({
      name: fee.name,
      description: fee.description,
      amount: fee.amount,
      category: fee.category,
      isMandatory: fee.isMandatory,
      frequency: fee.frequency,
      applicableClasses: fee.applicableClasses || []
    });
    setShowAddFeeModal(true);
  };

  const resetNewFee = () => {
    setNewFee({
      name: '',
      description: '',
      amount: '',
      category: 'academic',
      isMandatory: false,
      frequency: 'one-time',
      applicableClasses: []
    });
  };

  // Payment Management
  const handleRecordPayment = async () => {
    if (!newPayment.username || !newPayment.feeType || !newPayment.amount) {
      alert('Please fill in all required fields');
      return;
    }

    try {
      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/api/fees/payments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newPayment)
      });

      if (response.ok) {
        alert('Payment recorded successfully!');
        setShowRecordPaymentModal(false);
        resetNewPayment();
        loadPayments();
        loadAnalytics();
      } else {
        alert('Failed to record payment');
      }
    } catch (error) {
      console.error('Error recording payment:', error);
      alert('Failed to record payment');
    }
  };

  const handleDeletePayment = async (paymentId) => {
    if (!window.confirm('Are you sure you want to delete this payment record?')) {
      return;
    }

    try {
      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/api/fees/payments/${paymentId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        alert('Payment deleted successfully!');
        loadPayments();
        loadAnalytics();
      } else {
        alert('Failed to delete payment');
      }
    } catch (error) {
      console.error('Error deleting payment:', error);
      alert('Failed to delete payment');
    }
  };

  const resetNewPayment = () => {
    setNewPayment({
      username: '',
      feeType: '',
      amount: '',
      paymentMethod: 'Cash',
      transactionId: '',
      notes: ''
    });
  };

  const applyPaymentFilters = () => {
    let filtered = [...payments];

    if (paymentFilters.status !== 'all') {
      filtered = filtered.filter(p => p.status === paymentFilters.status);
    }

    if (paymentFilters.feeType !== 'all') {
      filtered = filtered.filter(p => p.feeType === paymentFilters.feeType);
    }

    if (paymentFilters.student) {
      filtered = filtered.filter(p =>
        p.username.toLowerCase().includes(paymentFilters.student.toLowerCase()) ||
        p.studentName.toLowerCase().includes(paymentFilters.student.toLowerCase())
      );
    }

    if (paymentFilters.startDate) {
      filtered = filtered.filter(p =>
        new Date(p.paymentDate || p.date) >= new Date(paymentFilters.startDate)
      );
    }

    if (paymentFilters.endDate) {
      filtered = filtered.filter(p =>
        new Date(p.paymentDate || p.date) <= new Date(paymentFilters.endDate)
      );
    }

    setFilteredPayments(filtered);
  };

  useEffect(() => {
    applyPaymentFilters();
  }, [payments, paymentFilters]);

  // Bulk Operations
  const handleBulkGenerateFees = async () => {
    try {
      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/api/fees/bulk/generate`, {
        method: 'POST'
      });

      if (response.ok) {
        const result = await response.json();
        alert(result.message);
        setShowBulkModal(false);
      } else {
        alert('Failed to generate fees');
      }
    } catch (error) {
      console.error('Error generating fees:', error);
      alert('Failed to generate fees');
    }
  };

  const exportPayments = () => {
    const csvContent = [
      ['Student Name', 'Username', 'Fee Type', 'Amount', 'Payment Method', 'Status', 'Date', 'Transaction ID'],
      ...filteredPayments.map(p => [
        p.studentName,
        p.username,
        p.feeType,
        p.amount,
        p.paymentMethod,
        p.status,
        new Date(p.paymentDate || p.date).toLocaleDateString(),
        p.transactionId || 'N/A'
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `fee-payments-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading fee management...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Modern Fee Management</h1>
            <p className="text-gray-600 mt-2">Comprehensive fee collection and management system</p>
          </div>
          <button
            onClick={() => navigate('/dashboard')}
            className="bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-700"
          >
            Back to Dashboard
          </button>
        </div>

        {/* Analytics Cards */}
        {analytics && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-800">Total Collected</h3>
              <p className="text-3xl font-bold text-green-600">
                ₦{analytics.summary.totalCollected.toLocaleString()}
              </p>
              <p className="text-sm text-gray-600">This academic year</p>
            </div>

            <div className="bg-sky-50 border border-sky-200 rounded-xl shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-800">Collection Rate</h3>
              <p className="text-3xl font-bold text-blue-600">
                {analytics.summary.collectionRate}%
              </p>
              <p className="text-sm text-gray-600">Of expected revenue</p>
            </div>

            <div className="bg-rose-50 border border-rose-200 rounded-xl shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-800">Outstanding</h3>
              <p className="text-3xl font-bold text-red-600">
                ₦{analytics.summary.outstanding.toLocaleString()}
              </p>
              <p className="text-sm text-gray-600">Remaining balance</p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-xl shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-800">Defaulters</h3>
              <p className="text-3xl font-bold text-orange-600">
                {defaulters.length}
              </p>
              <p className="text-sm text-gray-600">Students with balances</p>
            </div>
          </div>
        )}

        {/* Navigation Tabs */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 mb-8">
          <div className="p-3 border-b border-slate-100">
            <nav className="flex flex-wrap gap-2">
              {[
                { id: 'structures', label: 'Fee Structures', icon: '🏠' },
                { id: 'payments', label: 'Payment Management', icon: '💳' },
                { id: 'analytics', label: 'Analytics & Reports', icon: 'Stats' },
                { id: 'bulk', label: 'Bulk Operations', icon: '⚡' }
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`px-4 py-3 text-sm font-bold flex items-center space-x-2 rounded-lg transition ${
                    activeTab === tab.id
                      ? 'bg-slate-950 text-white shadow'
                      : 'text-slate-600 bg-slate-50 hover:bg-amber-50 hover:text-amber-800'
                  }`}
                >
                  <span>{tab.icon}</span>
                  <span>{tab.label}</span>
                </button>
              ))}
            </nav>
          </div>

          <div className="p-6">
            {/* Fee Structures Tab */}
            {activeTab === 'structures' && (
              <div>
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-xl font-bold text-gray-800">Fee Structures</h2>
                  <button
                    onClick={() => setShowAddFeeModal(true)}
                    className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
                  >
                    + Add Fee Structure
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {feeStructures.map((fee) => (
                    <div key={fee.id} className="bg-white border rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow">
                      <div className="flex justify-between items-start mb-4">
                        <div>
                          <h3 className="font-bold text-lg text-gray-800">{fee.name}</h3>
                          <p className="text-gray-600 text-sm">{fee.description}</p>
                        </div>
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleEditFee(fee)}
                            className="text-blue-600 hover:text-blue-800 text-sm"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDeleteFee(fee.id)}
                            className="text-red-600 hover:text-red-800 text-sm"
                          >
                            Delete
                          </button>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Amount:</span>
                          <span className="font-semibold text-lg text-green-600">
                            ₦{parseFloat(fee.amount).toLocaleString()}
                          </span>
                        </div>

                        <div className="flex justify-between">
                          <span className="text-gray-600">Category:</span>
                          <span className={`px-2 py-1 rounded text-xs ${
                            fee.category === 'academic' ? 'bg-blue-100 text-blue-800' :
                            fee.category === 'materials' ? 'bg-green-100 text-green-800' :
                            fee.category === 'transport' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-purple-100 text-purple-800'
                          }`}>
                            {fee.category}
                          </span>
                        </div>

                        <div className="flex justify-between">
                          <span className="text-gray-600">Frequency:</span>
                          <span className="text-sm">{fee.frequency}</span>
                        </div>

                        <div className="flex justify-between">
                          <span className="text-gray-600">Type:</span>
                          <span className={`px-2 py-1 rounded text-xs ${
                            fee.isMandatory ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-800'
                          }`}>
                            {fee.isMandatory ? 'Mandatory' : 'Optional'}
                          </span>
                        </div>

                        {fee.applicableClasses && fee.applicableClasses.length > 0 && (
                          <div>
                            <span className="text-gray-600">Classes:</span>
                            <div className="flex flex-wrap gap-1 mt-1">
                              {fee.applicableClasses.map(cls => (
                                <span key={cls} className="px-2 py-1 bg-blue-50 text-blue-700 rounded text-xs">
                                  {cls}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Payment Management Tab */}
            {activeTab === 'payments' && (
              <div>
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-xl font-bold text-gray-800">Payment Management</h2>
                  <div className="flex space-x-3">
                    <button
                      onClick={exportPayments}
                      className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
                    >
                      Export CSV
                    </button>
                    <button
                      onClick={() => setShowRecordPaymentModal(true)}
                      className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
                    >
                      + Record Payment
                    </button>
                  </div>
                </div>

                {/* Payment Filters */}
                <div className="bg-gray-50 rounded-lg p-4 mb-6">
                  <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                    <select
                      value={paymentFilters.status}
                      onChange={(e) => setPaymentFilters({...paymentFilters, status: e.target.value})}
                      className="border rounded px-3 py-2"
                    >
                      <option value="all">All Status</option>
                      <option value="completed">Completed</option>
                      <option value="pending">Pending</option>
                      <option value="failed">Failed</option>
                    </select>

                    <select
                      value={paymentFilters.feeType}
                      onChange={(e) => setPaymentFilters({...paymentFilters, feeType: e.target.value})}
                      className="border rounded px-3 py-2"
                    >
                      <option value="all">All Fee Types</option>
                      {feeStructures.map(fee => (
                        <option key={fee.id} value={fee.name}>{fee.name}</option>
                      ))}
                    </select>

                    <input
                      type="text"
                      placeholder="Search student..."
                      value={paymentFilters.student}
                      onChange={(e) => setPaymentFilters({...paymentFilters, student: e.target.value})}
                      className="border rounded px-3 py-2"
                    />

                    <input
                      type="date"
                      value={paymentFilters.startDate}
                      onChange={(e) => setPaymentFilters({...paymentFilters, startDate: e.target.value})}
                      className="border rounded px-3 py-2"
                    />

                    <input
                      type="date"
                      value={paymentFilters.endDate}
                      onChange={(e) => setPaymentFilters({...paymentFilters, endDate: e.target.value})}
                      className="border rounded px-3 py-2"
                    />
                  </div>
                </div>

                {/* Payments Table */}
                <div className="overflow-x-auto">
                  <table className="min-w-full bg-white border border-gray-200">
                    <thead>
                      <tr className="bg-gray-50">
                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Student</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Fee Type</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Amount</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Method</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Status</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Date</th>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredPayments.map((payment) => (
                        <tr key={payment.id} className="border-t hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm">
                            <div>
                              <div className="font-medium text-gray-800">{payment.studentName}</div>
                              <div className="text-gray-500">@{payment.username}</div>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-800">{payment.feeType}</td>
                          <td className="px-4 py-3 text-sm font-semibold text-green-600">
                            ₦{parseFloat(payment.amount).toLocaleString()}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600">{payment.paymentMethod}</td>
                          <td className="px-4 py-3 text-sm">
                            <span className={`px-2 py-1 rounded text-xs ${
                              payment.status === 'completed' ? 'bg-green-100 text-green-800' :
                              payment.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                              'bg-red-100 text-red-800'
                            }`}>
                              {payment.status}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600">
                            {new Date(payment.paymentDate || payment.date).toLocaleDateString()}
                          </td>
                          <td className="px-4 py-3 text-sm">
                            <button
                              onClick={() => handleDeletePayment(payment.id)}
                              className="text-red-600 hover:text-red-800"
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Analytics Tab */}
            {activeTab === 'analytics' && (
              <div>
                <h2 className="text-xl font-bold text-gray-800 mb-6">Analytics & Reports</h2>

                {analytics && (
                  <div className="space-y-8">
                    {/* Monthly Trends */}
                    <div className="bg-white rounded-lg shadow p-6">
                      <h3 className="text-lg font-semibold text-gray-800 mb-4">Monthly Payment Trends</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {analytics.monthlyTrends.map((month) => (
                          <div key={month.month} className="bg-gray-50 rounded p-4">
                            <div className="text-sm font-medium text-gray-600">{month.month}</div>
                            <div className="text-lg font-bold text-green-600">
                              ₦{month.revenue.toLocaleString()}
                            </div>
                            <div className="text-sm text-gray-500">{month.payments} payments</div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Defaulters List */}
                    <div className="bg-white rounded-lg shadow p-6">
                      <h3 className="text-lg font-semibold text-gray-800 mb-4">Students with Outstanding Balances</h3>
                      <div className="overflow-x-auto">
                        <table className="min-w-full">
                          <thead>
                            <tr className="border-b">
                              <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Student</th>
                              <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Class</th>
                              <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Expected</th>
                              <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Paid</th>
                              <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Balance</th>
                              <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Payment Rate</th>
                            </tr>
                          </thead>
                          <tbody>
                            {defaulters.slice(0, 20).map((student) => (
                              <tr key={student.username} className="border-b hover:bg-gray-50">
                                <td className="px-4 py-3 text-sm font-medium text-gray-800">
                                  {student.name}
                                </td>
                                <td className="px-4 py-3 text-sm text-gray-600">{student.className}</td>
                                <td className="px-4 py-3 text-sm font-medium">
                                  ₦{student.expected.toLocaleString()}
                                </td>
                                <td className="px-4 py-3 text-sm font-medium text-green-600">
                                  ₦{student.paid.toLocaleString()}
                                </td>
                                <td className="px-4 py-3 text-sm font-medium text-red-600">
                                  ₦{student.balance.toLocaleString()}
                                </td>
                                <td className="px-4 py-3 text-sm">
                                  <span className={`px-2 py-1 rounded text-xs ${
                                    student.paymentRate >= 80 ? 'bg-green-100 text-green-800' :
                                    student.paymentRate >= 50 ? 'bg-yellow-100 text-yellow-800' :
                                    'bg-red-100 text-red-800'
                                  }`}>
                                    {student.paymentRate}%
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Bulk Operations Tab */}
            {activeTab === 'bulk' && (
              <div>
                <h2 className="text-xl font-bold text-gray-800 mb-6">Bulk Operations</h2>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-white rounded-lg shadow p-6">
                    <h3 className="text-lg font-semibold text-gray-800 mb-4">Generate Fees for All Students</h3>
                    <p className="text-gray-600 mb-4">
                      Automatically create fee records for all students based on applicable fee structures.
                    </p>
                    <button
                      onClick={handleBulkGenerateFees}
                      className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
                    >
                      Generate Fees
                    </button>
                  </div>

                  <div className="bg-white rounded-lg shadow p-6">
                    <h3 className="text-lg font-semibold text-gray-800 mb-4">Bulk Payment Recording</h3>
                    <p className="text-gray-600 mb-4">
                      Record multiple payments at once using CSV import or manual entry.
                    </p>
                    <button
                      onClick={() => setShowBulkModal(true)}
                      className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
                    >
                      Bulk Record Payments
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Add/Edit Fee Modal */}
      {showAddFeeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold text-gray-900">
                  {editingFee ? 'Edit Fee Structure' : 'Add New Fee Structure'}
                </h2>
                <button
                  onClick={() => {
                    setShowAddFeeModal(false);
                    setEditingFee(null);
                    resetNewFee();
                  }}
                  className="text-gray-400 hover:text-gray-600 text-2xl"
                >
                  Ã—
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Fee Name *
                  </label>
                  <input
                    type="text"
                    value={newFee.name}
                    onChange={(e) => setNewFee({...newFee, name: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., Tuition Fee"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    value={newFee.description}
                    onChange={(e) => setNewFee({...newFee, description: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows="3"
                    placeholder="Optional description"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Amount (₦) *
                    </label>
                    <input
                      type="number"
                      value={newFee.amount}
                      onChange={(e) => setNewFee({...newFee, amount: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="0.00"
                      min="0"
                      step="0.01"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Category *
                    </label>
                    <select
                      value={newFee.category}
                      onChange={(e) => setNewFee({...newFee, category: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    >
                      <option value="academic">Academic</option>
                      <option value="materials">Materials</option>
                      <option value="transport">Transport</option>
                      <option value="meals">Meals</option>
                      <option value="activities">Activities</option>
                      <option value="admission">Admission</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Frequency
                    </label>
                    <select
                      value={newFee.frequency}
                      onChange={(e) => setNewFee({...newFee, frequency: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="one-time">One-time</option>
                      <option value="monthly">Monthly</option>
                      <option value="quarterly">Quarterly</option>
                      <option value="annually">Annually</option>
                    </select>
                  </div>

                  <div className="flex items-center space-x-2 pt-8">
                    <input
                      type="checkbox"
                      id="isMandatory"
                      checked={newFee.isMandatory}
                      onChange={(e) => setNewFee({...newFee, isMandatory: e.target.checked})}
                      className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <label htmlFor="isMandatory" className="text-sm text-gray-700">
                      Mandatory Fee
                    </label>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Applicable Classes
                  </label>
                  <div className="border border-gray-300 rounded-md p-3 max-h-32 overflow-y-auto">
                    <div className="grid grid-cols-2 gap-2">
                      {classes.map((cls) => (
                        <label key={cls} className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            checked={newFee.applicableClasses.includes(cls)}
                            onChange={(e) => {
                              const updatedClasses = e.target.checked
                                ? [...newFee.applicableClasses, cls]
                                : newFee.applicableClasses.filter(c => c !== cls);
                              setNewFee({...newFee, applicableClasses: updatedClasses});
                            }}
                            className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                          />
                          <span className="text-sm text-gray-700">{cls}</span>
                        </label>
                      ))}
                    </div>
                    <p className="text-xs text-gray-500 mt-2">
                      Leave empty to apply to all classes
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex justify-end space-x-3 mt-6 pt-4 border-t">
                <button
                  onClick={() => {
                    setShowAddFeeModal(false);
                    setEditingFee(null);
                    resetNewFee();
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveFee}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  {editingFee ? 'Update Fee' : 'Create Fee'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Record Payment Modal */}
      {showRecordPaymentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold text-gray-900">Record Payment</h2>
                <button
                  onClick={() => {
                    setShowRecordPaymentModal(false);
                    resetNewPayment();
                  }}
                  className="text-gray-400 hover:text-gray-600 text-2xl"
                >
                  Ã—
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Student Username *
                  </label>
                  <input
                    type="text"
                    value={newPayment.username}
                    onChange={(e) => setNewPayment({...newPayment, username: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter student username"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Fee Type *
                  </label>
                  <select
                    value={newPayment.feeType}
                    onChange={(e) => setNewPayment({...newPayment, feeType: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    <option value="">Select Fee Type</option>
                    {feeStructures.map(fee => (
                      <option key={fee.id} value={fee.name}>
                        {fee.name} - ₦{parseFloat(fee.amount).toLocaleString()}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Amount (₦) *
                  </label>
                  <input
                    type="number"
                    value={newPayment.amount}
                    onChange={(e) => setNewPayment({...newPayment, amount: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="0.00"
                    min="0"
                    step="0.01"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Payment Method
                  </label>
                  <select
                    value={newPayment.paymentMethod}
                    onChange={(e) => setNewPayment({...newPayment, paymentMethod: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="Cash">Cash</option>
                    <option value="Bank Transfer">Bank Transfer</option>
                    <option value="Cheque">Cheque</option>
                    <option value="Online Payment">Online Payment</option>
                    <option value="Card">Card</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Transaction ID/Reference
                  </label>
                  <input
                    type="text"
                    value={newPayment.transactionId}
                    onChange={(e) => setNewPayment({...newPayment, transactionId: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Optional transaction reference"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Notes
                  </label>
                  <textarea
                    value={newPayment.notes}
                    onChange={(e) => setNewPayment({...newPayment, notes: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows="3"
                    placeholder="Optional notes"
                  />
                </div>
              </div>

              <div className="flex justify-end space-x-3 mt-6 pt-4 border-t">
                <button
                  onClick={() => {
                    setShowRecordPaymentModal(false);
                    resetNewPayment();
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleRecordPayment}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  Record Payment
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminFeeManagement;
